import { objectsAreEqualIgnoreUndefined } from '@sequencehq/utils'
import { isEqual } from '@sequencehq/utils/dates'
import {
  quoteStatusAdapter,
  scheduleStatusAdapter
} from 'modules/Cube/utils/status.adapter'
import { CubeReducerState } from 'modules/Cube/domain/cube.domain.types'

type CriticalPathSharedData = Pick<
  CubeReducerState['data'],
  'phases' | 'minimums' | 'discounts' | 'creditGrants'
>

type CriticalPathScheduleData = CriticalPathSharedData &
  Pick<CubeReducerState['data'], 'common' | 'schedule'>

type CriticalPathQuoteTemplateData = CriticalPathSharedData &
  Pick<CubeReducerState['data'], 'quote'> & {
    common: Omit<CubeReducerState['data']['common'], 'customerId'>
  }

/**
 * This query is used to check whether or not critical path data is different vs.
 * the initial load. It should contain the minimal set of data to determine whether
 * or not an edit has been made, and otherwise ignore data.
 *
 * Therefore, 'supporting' data such as customer are not required here, since
 * the change of id in the critical path billing schedule will be enough to indicate
 * a difference.
 * @param prevData
 * @returns
 */
export const getCriticalPathData = (
  prevData: CubeReducerState['data']
): CriticalPathScheduleData | CriticalPathQuoteTemplateData => {
  const isASchedule = Boolean(scheduleStatusAdapter.out(prevData.common.status))

  return isASchedule
    ? getCriticalPathScheduleData(prevData)
    : getCriticalPathQuoteTemplateData(prevData)
}

const getCriticalPathSharedData = (
  prevData: CubeReducerState['data']
): CriticalPathSharedData => ({
  phases: prevData.phases,
  minimums: prevData.minimums,
  discounts: prevData.discounts,
  // Editing a grant in place changes only this map — the phase keeps the same
  // creditGrantIds — so it has to be part of the critical path.
  creditGrants: prevData.creditGrants
})

export const getCriticalPathScheduleData = (
  prevData: CubeReducerState['data']
): CriticalPathScheduleData => ({
  ...getCriticalPathSharedData(prevData),
  common: prevData.common,
  schedule: prevData.schedule
})

/**
 * A template has no customer, and cleanup nulls an id resolving to no loaded
 * customer — so `customerId` here would report that normalisation as an edit.
 * Attachments, deal type and expiry are persisted from `quote`.
 */
export const getCriticalPathQuoteTemplateData = (
  prevData: CubeReducerState['data']
): CriticalPathQuoteTemplateData => {
  const { customerId: _customerId, ...common } = prevData.common

  return {
    ...getCriticalPathSharedData(prevData),
    common,
    quote: prevData.quote
  }
}

export const hasCubeBeenUpdated = (prevState: CubeReducerState) => {
  // A quote syncs its own edits through `useQuoteEditorHeader`, so the critical
  // path speaks only for schedules and quote templates.
  if (quoteStatusAdapter.out(prevState.data.common.status)) {
    return false
  }

  const cubeEqual = objectsAreEqualIgnoreUndefined(
    getCriticalPathData(prevState.initialData),
    getCriticalPathData(prevState.data)
  )

  if (
    !cubeEqual ||
    !prevState.initialData.common.startDate ||
    !prevState.data.common.startDate
  ) {
    return true
  }

  /**
   * Dates are not properly covered by dequal (which powers the base equality check).
   * We've recently discussed moving towards our own date library, which would be
   * string based, but until now we need to specifically account for any dates that
   * need comparing. If more than one or two dates get added before we shift to a
   * new library, then it may be worth doing a more involved, general, solution that
   * can detect and compare date objects within the structures.
   *
   * However, for now, since start date is the only date that can be directly edited
   * this is fine!
   */
  const startDateChanged = isEqual(
    prevState.initialData.common.startDate,
    prevState.data.common.startDate
  )

  return !startDateChanged
}
